import {
  DIFFICULTY_HINT_BY_CHOICE_LABEL,
  QUIZ_TYPE_HINT_BY_CHOICE_LABEL,
} from '../constants';

import { type AiBuilderQuizTypeHint, type AiBuilderTurnHints } from './types';

const SOURCE_IMPORT_DECISION_SLOT = 'source_import_decision';
const SOURCE_IMPORT_CHOICE_IMPORT = '문제 가져오기';
const SOURCE_IMPORT_CHOICE_GENERATE = '새로 만들기';
const COUNT_MISSING_SLOT = 'count';
const COUNT_PRESET_VALUES = new Set(['5', '10', '15', '20']);

const QUIZ_TYPE_HINT_VALUES: ReadonlySet<string> = new Set([
  'ox',
  'multiple',
  'subjective',
  'essay',
]);

type AiBuilderChoiceTurnHintMessage = {
  missingSlot?: string;
  options: Array<{
    label: string;
    value?: string;
  }>;
};

/**
 * option 하나를 quiz-type hint 로 해석한다.
 * canonical value(label→value 계약)가 1차 신호이고, 라벨 매핑은 value 없이
 * 오던 구 계약 메시지(재열람된 이력 등) 하위호환 폴백이다.
 */
const getQuizTypeHint = (option: {
  label: string;
  value?: string;
}): AiBuilderQuizTypeHint | undefined => {
  if (option.value && QUIZ_TYPE_HINT_VALUES.has(option.value)) {
    return option.value as AiBuilderQuizTypeHint;
  }

  return QUIZ_TYPE_HINT_BY_CHOICE_LABEL[option.label];
};

export const createChoiceTurnHints = ({
  message,
  selectedLabels,
}: {
  message: AiBuilderChoiceTurnHintMessage;
  selectedLabels: string[];
}): AiBuilderTurnHints | undefined => {
  const optionLabels = message.options.map(option => option.label);
  const optionLabelSet = new Set(optionLabels);
  const selectedLabelsFromMessage = selectedLabels.every(label =>
    optionLabelSet.has(label),
  );

  if (!selectedLabelsFromMessage) return undefined;

  if (
    message.missingSlot === COUNT_MISSING_SLOT &&
    selectedLabels.length === 1
  ) {
    const selectedOption = message.options.find(
      option => option.label === selectedLabels[0],
    );
    const value = selectedOption?.value;
    if (value && COUNT_PRESET_VALUES.has(value)) {
      return { count: Number(value) };
    }
    return undefined;
  }

  if (
    message.missingSlot === SOURCE_IMPORT_DECISION_SLOT &&
    selectedLabels.length === 1
  ) {
    if (selectedLabels[0] === SOURCE_IMPORT_CHOICE_IMPORT) {
      return { sourceImportDecision: 'import_questions' };
    }

    if (selectedLabels[0] === SOURCE_IMPORT_CHOICE_GENERATE) {
      return { sourceImportDecision: 'generate_new' };
    }
  }

  const quizTypeHintByLabel = new Map<string, AiBuilderQuizTypeHint>();
  for (const option of message.options) {
    const quizTypeHint = getQuizTypeHint(option);
    if (quizTypeHint !== undefined) {
      quizTypeHintByLabel.set(option.label, quizTypeHint);
    }
  }

  const allOptionsAreQuizTypes = optionLabels.every(label =>
    quizTypeHintByLabel.has(label),
  );

  if (allOptionsAreQuizTypes) {
    const quizTypes = selectedLabels
      .map(label => quizTypeHintByLabel.get(label))
      .filter(
        (quizType): quizType is AiBuilderQuizTypeHint => quizType !== undefined,
      );

    if (quizTypes.length > 0 && quizTypes.length === selectedLabels.length) {
      return { quizTypes };
    }
  }

  const allOptionsAreDifficulties = optionLabels.every(
    label => DIFFICULTY_HINT_BY_CHOICE_LABEL[label] !== undefined,
  );

  if (allOptionsAreDifficulties && selectedLabels.length === 1) {
    const difficulty = DIFFICULTY_HINT_BY_CHOICE_LABEL[selectedLabels[0]];

    if (difficulty !== undefined) {
      return { difficulty };
    }
  }

  return undefined;
};
